import type { Control } from "react-hook-form";
import { CalendarIcon, ChevronUp, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import DatePicker from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import type { LicenseOnboardingFormValues } from "./LicensesOnboardingForm";
import {
  ONBOARDING_FORM_STYLES as styles,
  ONBOARDING_SELECT_CLASS_NAMES,
} from "./onboarding-form-styles";

export function LicenseExpirationInput({
  value,
  onChange,
  onBlur,
}: {
  value: Date | null;
  onChange: (date: Date | null) => void;
  onBlur?: () => void;
}) {
  const { formItemId, formDescriptionId, formMessageId, error } =
    useFormField();
  return (
    <div className="relative">
      <DatePicker
        id={formItemId}
        selected={value}
        onChange={onChange}
        onBlur={onBlur}
        ariaInvalid={error ? "true" : "false"}
        ariaDescribedBy={
          error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId
        }
        customInput={
          <Input
            value={value ? format(value, "MM/dd/yyyy") : ""}
            placeholder="MM/DD/YYYY"
            className={`${styles.input} pr-11`}
          />
        }
        dateFormat="MM/dd/yyyy"
        placeholderText="MM/DD/YYYY"
        wrapperClassName="w-full"
        minDate={new Date()}
      />
      <CalendarIcon
        className="pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-slate-400"
        aria-hidden="true"
      />
    </div>
  );
}

export function OnboardingStateLicenseCard({
  control,
  index,
  readOnlyState,
  expanded,
  onRemove,
  onToggle,
}: {
  control: Control<LicenseOnboardingFormValues>;
  index: number;
  readOnlyState: boolean;
  expanded: boolean;
  onRemove: () => void;
  onToggle: () => void;
}) {
  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-slate-500">
          License {index + 1}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-11 gap-1.5 rounded-lg px-2 text-slate-500 hover:bg-red-50 hover:text-red-700"
          aria-label={`Remove license ${index + 1}`}
          onClick={onRemove}
        >
          <Trash2 className="size-4" aria-hidden="true" />
          Remove
        </Button>
      </div>
      <FormField
        control={control}
        name={`stateLicenses.${index}.state`}
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel className={styles.label}>
              State{" "}
              <span className="text-destructive" aria-hidden="true">
                *
              </span>
            </FormLabel>
            <FormControl>
              {readOnlyState ? (
                <Input {...field} readOnly className={styles.input} />
              ) : (
                <USStateSelect
                  {...field}
                  classNames={ONBOARDING_SELECT_CLASS_NAMES}
                />
              )}
            </FormControl>
            <FormMessage />
            {readOnlyState && (
              <FormDescription className="text-xs leading-5 text-slate-500">
                To use a different state, remove this license and add another.
              </FormDescription>
            )}
          </FormItem>
        )}
      />
      <Collapsible open={expanded} onOpenChange={onToggle}>
        {!expanded && (
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="link"
              size="none"
              className="min-h-11 gap-1.5 whitespace-normal p-0 text-left text-sm font-medium underline-offset-4"
            >
              <Plus className="size-4 shrink-0" aria-hidden="true" />
              Add license number & expiration (optional)
            </Button>
          </CollapsibleTrigger>
        )}
        <CollapsibleContent className="space-y-3 pt-1">
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={control}
              name={`stateLicenses.${index}.license_number`}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className={styles.label}>
                    License Number{" "}
                    <span className="font-normal text-slate-500">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter license number"
                      className={styles.input}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`stateLicenses.${index}.expiration_date`}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className={styles.label}>
                    Expiration Date{" "}
                    <span className="font-normal text-slate-500">
                      (optional)
                    </span>
                  </FormLabel>
                  <LicenseExpirationInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="none"
              className="min-h-11 gap-1.5 p-0 text-sm font-normal text-slate-500 hover:bg-transparent hover:text-slate-700"
            >
              <ChevronUp className="size-4" aria-hidden="true" />
              Hide details
            </Button>
          </CollapsibleTrigger>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
